/**
 * Publishes the width of the page's reserved scrollbar gutter as the CSS custom
 * property `--app-scrollbar-width`.
 *
 * Why this exists: `html` carries `scrollbar-gutter: stable` (see
 * styles/tailwind.css), so the html box is permanently narrower than the
 * viewport by the gutter width. When HeadlessUI's Dialog locks scrolling it sets
 * inline `overflow: hidden`, Chrome stops reserving the gutter, and the app
 * would either leave an empty band outside the html box or — once the gutter is
 * released — widen by the gutter width and visibly shift. HeadlessUI ships its
 * own `padding-right` compensation, but it measures after releasing the gutter
 * and so reports 0 here. Measuring ourselves keeps the content box exactly the
 * same width whether a modal is open or not.
 */
const CSS_VAR = "--app-scrollbar-width";

const isScrollLocked = () =>
  document.documentElement.style.overflow === "hidden";

const measure = () => {
  const html = document.documentElement;
  // Only meaningful while the gutter is actually reserved; under the scroll lock
  // it is released and we would publish 0.
  if (isScrollLocked()) return;
  // `clientWidth` reads inconsistently for the root element under
  // `scrollbar-gutter: stable`, so compare the viewport against the html box
  // itself — that difference is the reserved gutter.
  const width = Math.round(
    window.innerWidth - html.getBoundingClientRect().width
  );
  if (width >= 0) html.style.setProperty(CSS_VAR, `${width}px`);
};

export const trackScrollbarGutter = () => {
  measure();
  // Layout is not necessarily settled at module evaluation time.
  window.addEventListener("load", measure);
  window.addEventListener("resize", measure);
  // Re-measure once each scroll lock is released, so a stale value can never
  // outlive a change in scrollbar width (zoom, OS setting, devtools docking).
  new MutationObserver(() => {
    if (!isScrollLocked()) measure();
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["style"],
  });
};
